import type {
  MfaEnrollmentCompletedResponseDto,
  MfaEnrollmentResponseDto
} from '@idc-forms/api-client'
import { useMutation } from '@tanstack/react-query'
import { Copy, Loader2, ShieldCheck } from 'lucide-react'
import type { ReactNode } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { apiTokenService } from '../api-token.service'
import { showApiTokenError } from '../api-token-error'

type MfaEnrollmentProps = {
  accessToken: string
  enrollment: MfaEnrollmentResponseDto
  onCompleted: (result: MfaEnrollmentCompletedResponseDto) => void
  description?: ReactNode
  verifyEnrollment?: (
    accessToken: string,
    code: string
  ) => Promise<MfaEnrollmentCompletedResponseDto>
}

type EnrollmentForm = {
  code: string
}

export function MfaEnrollment({
  accessToken,
  enrollment,
  onCompleted,
  description,
  verifyEnrollment = (token, code) => apiTokenService.completeMfaEnrollment(token, { code })
}: MfaEnrollmentProps) {
  const { control, handleSubmit } = useForm<EnrollmentForm>({ defaultValues: { code: '' } })
  const mutation = useMutation({
    mutationFn: ({ code }: EnrollmentForm) => verifyEnrollment(accessToken, code),
    onSuccess: onCompleted
  })

  const submit = (values: EnrollmentForm) => {
    mutation.mutate(values, {
      onError: (error) => showApiTokenError(error, () => submit(values))
    })
  }

  const copyManualKey = async () => {
    try {
      await navigator.clipboard.writeText(enrollment.manualEntryKey)
      toast.success('Clave copiada.')
    } catch {
      toast.error('No se pudo copiar. Selecciona la clave manualmente.')
    }
  }

  return (
    <Card className="max-w-3xl">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2 text-primary">
            <ShieldCheck className="size-5" />
          </div>
          <div>
            <CardTitle>Activa la autenticación de dos factores</CardTitle>
            <CardDescription>
              {description ??
                'Los API tokens son credenciales de larga duración; para crearlos primero debes proteger tu cuenta con una aplicación de autenticación.'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-6 md:grid-cols-[auto_1fr]">
        <div className="w-fit rounded-lg border bg-white p-3">
          <img
            src={enrollment.qrCodeDataUrl}
            alt="Código QR para configurar la aplicación de autenticación"
            className="size-48"
          />
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          <div className="space-y-2">
            <p className="text-sm font-medium">1. Escanea el código con tu aplicación 2FA</p>
            <p className="text-sm text-muted-foreground">
              Si no puedes escanearlo, ingresa esta clave manualmente:
            </p>
            <div className="flex max-w-md gap-2">
              <Input
                value={enrollment.manualEntryKey}
                readOnly
                aria-label="Clave manual de autenticación"
                className="font-mono text-xs"
              />
              <Button type="button" variant="outline" onClick={copyManualKey}>
                <Copy className="mr-2 size-4" />
                Copiar
              </Button>
            </div>
          </div>

          <form className="flex max-w-md flex-col gap-4" onSubmit={handleSubmit(submit)}>
            <Controller
              control={control}
              name="code"
              rules={{
                required: 'Ingresa el código de seis dígitos.',
                pattern: { value: /^\d{6}$/, message: 'El código debe tener seis dígitos.' }
              }}
              render={({ field, fieldState }) => (
                <Field data-invalid={Boolean(fieldState.error)}>
                  <FieldLabel htmlFor="mfa-enrollment-code">
                    2. Confirma el primer código
                  </FieldLabel>
                  <Input
                    {...field}
                    id="mfa-enrollment-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    className="font-mono tracking-wider"
                  />
                  <FieldDescription>
                    La cuenta no queda protegida hasta que verifiquemos este código.
                  </FieldDescription>
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />
            <Button type="submit" disabled={mutation.isPending} className="w-fit">
              {mutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
              Activar 2FA
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  )
}
